import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, ChangeDetectionStrategy, Inject, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { SnShare } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SN_DRAWER_DATA, SnDrawerRef } from '@lib/ui/drawer';

import { ISnCanShare } from '../../interfaces/can-share';
import { SnShareFieldsGeneratorService } from '../../services/share-fields-generator.service';

export interface IShareConfigDrawerDataV2 {
  share: SnShare;
  updateHandler?: ISnCanShare['updateShare'];
}

@Component({
  selector: 'sn-edit-share-drawer-v2',
  templateUrl: './share-config-drawer-v2.container.html',
  styleUrls: ['./share-config-drawer-v2.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareConfigDrawerV2Container implements OnDestroy {
  public readonly form = new UntypedFormGroup({});
  public readonly formFields: FormlyFieldConfig[];
  public formModel: any = {};

  public readonly share: SnShare;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _drawerRef: SnDrawerRef,
    @Inject(SN_DRAWER_DATA) private readonly _data: IShareConfigDrawerDataV2,
    private readonly _shareFieldsGenerator: SnShareFieldsGeneratorService,
  ) {
    this.share = this._data.share;

    this.formFields = this._getFormFields();
    this.formModel = this._data.share.toJSON({ groups: ['form'] });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onFormSubmit(): void {
    if (this.form.invalid || !this._data.updateHandler) {
      return;
    }

    this._data
      .updateHandler(this.share.object.id, this.share.id, this.formModel)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (share: SnShare) => {
          this._drawerRef.close(share);
        },
        error: (err) => {
          console.error('Failed to update share:', err);
        }
      });
  }

  private _getFormFields(): FormlyFieldConfig[] {
    return [
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.viewAccess'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'view-access-group',
            fieldGroup: [
              this._shareFieldsGenerator.getViewLiveVideoFieldV2(),
              this._shareFieldsGenerator.getViewRecordingsFieldV2(),
              this._shareFieldsGenerator.getRestrictedFieldV2(),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.controlAccess'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'control-access-row',
            fieldGroup: [
              this._shareFieldsGenerator.getEnablePTZFieldV2(),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.roleAccess'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'role-access-row',
            fieldGroup: [
              this._shareFieldsGenerator.getAdminOnlyFieldV2(),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.accessSchedule'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'date-row',
            fieldGroup: [
              this._shareFieldsGenerator.getStartDateField({
                className: 'start-date-col',
                templateOptions: {
                  appearance: 'outline',
                },
              }),
              this._shareFieldsGenerator.getEndDateField({
                className: 'end-date-col',
                templateOptions: {
                  appearance: 'outline',
                },
              }),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.shareForm.dataDisclosure'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'data-disclosure-row',
            fieldGroup: [
              this._shareFieldsGenerator.getDataDisclosureField(),
            ],
          },
        ],
      },
    ];
  }
}

